import React, { useState, useEffect } from 'react';
import { Container, Paper, Box, TextField, Button, Snackbar, Alert, FormControlLabel, Switch, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { RouteDefine } from '../../routes/index.tsx/RouteDefine';
import { useApi } from '../../services/useApi';
import LoadingOverlay from '../../components/LoadingOverlay';
import { PageTitle, SectionTitle } from '../../components/TypographyStyles';
import { ApiSuccessResponse, ApiErrorResponse, isErrorResponse, ConnectionData } from '../../services/api.types';
import { authService } from '../../services/auth.service';
import { Layout } from '../../components/layout';
import { env } from 'process';
import { useRef } from 'react';
interface ConnectionPayload {
  economic: {
    token: string;
    name?: string;
    number?: string;
  };
}

type ConnectionResponse = ApiSuccessResponse<ConnectionData> | ApiErrorResponse;

const SetupConnectionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, post } = useApi();
  const [economicToken, setEconomicToken] = useState('');
  const [economicName, setEconomicName] = useState('');
  const [economicNumber, setEconomicNumber] = useState('');
  const [rememberConnection, setRememberConnection] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const [isGeneratingSetup, setIsGeneratingSetup] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string | React.ReactNode;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const  [triggerConnect, setTriggerConnect] = useState(false);

  // Redirect to home if already connected
  useEffect(() => {
    if (authService.isUserAuthenticated()) {
      navigate(`/${RouteDefine.home}`);
    }
  }, [navigate]);

  useEffect(() => {
    if (triggerConnect) {
      connectButtonRef.current?.click();
    }
  }, [triggerConnect]);

  // Load saved connection info if exists
  useEffect(() => {
    let tokenFromUrl = new URLSearchParams(window.location.search).get('token');
    if (tokenFromUrl) {
      setEconomicToken(tokenFromUrl);
      setRememberConnection(true);
      setTriggerConnect(true);
    }else{
      const loadConnectionInfo = () => {
        try {
          const savedInfo = localStorage.getItem('connection_info_login');
        if (savedInfo) {
          const connectionInfo = JSON.parse(savedInfo);
          setEconomicToken(connectionInfo.economic.token);
          setEconomicName(connectionInfo.economic.name || '');
          setEconomicNumber(connectionInfo.economic.number || '');
          setRememberConnection(true);
        }
      } catch (error) {
        console.error('Error loading connection info:', error);
      }
    };

      loadConnectionInfo();
    }
  }, []);

  // useEffect(() => {
  //   let token
  // }, [rememberConnection]);

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const saveConnectionInfo = (info: ConnectionPayload) => {
    localStorage.setItem('connection_info_login', JSON.stringify(info));
  };

  const handleConnect = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsConnecting(true);

    try {
      // Validate required fields
      if (!economicToken) {
        setNotification({
          open: true,
          message: 'Please fill in all required fields',
          severity: 'error'
        });
        return;
      }

      const payload: ConnectionPayload = {
        economic: {
          token: economicToken
        },
      };

      // Call API using useApi service
      const response = await post<ConnectionResponse>('/main/connection', payload);
      let timeout = 0;
      if (response.data.success) {
        // Save to storage if remember connection is enabled
        if (rememberConnection) {
          saveConnectionInfo(payload);
        } else {
          localStorage.removeItem('connection_info_login');
        }
        
        authService.setConnectionData(response.data.data);

        // Set connection data in auth service
        authService.setToken(response.data.data.token);
        let generateSetup = response.data.data.economic.generateSetup;

        if(generateSetup)
        {
          timeout = 10000;
          //generate setup and show notification
          setTimeout(() => {
            setNotification({
              open: true,
              message: 'Generating setup...',
              severity: 'success'
            });
          }, 1000);
          setIsGeneratingSetup(true);
          const response = await post<ApiSuccessResponse<any>>('/main/generate-setup',{});
          const setupData = response.data.data;
          const setupMessage = Object.entries(setupData)
            .map(([key, value]) => {
              const status = String(value).includes('already exists') ? 'success' : 'warning';
              const color = status === 'success' ? '#4caf50' : '#ff9800';
              return `<div style="color: ${color}">${key}: ${value}</div>`;
            })
            .join('');
          
          setNotification({
            open: true,
            message: <div dangerouslySetInnerHTML={{ __html: setupMessage }} />,
            severity: 'info'
          });
          
          timeout = 10000;
          // Show countdown message
          let countdown = timeout/1000;
          const countdownInterval = setInterval(() => {
            countdown--;
            if (countdown > 0) {
              setNotification({
                open: true,
                message: <div>
                  <div dangerouslySetInnerHTML={{ __html: setupMessage }} />
                  <div style={{ marginTop: '10px', fontWeight: 'bold' }}>
                    Redirecting in {countdown} seconds...
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <a href={from} style={{ color: 'blue', textDecoration: 'underline' }}>Click here to redirect now</a>
                  </div>
                </div>,
                severity: 'info'
              });
            } else {
              clearInterval(countdownInterval);
            }
          }, 1000);
        }
        // Navigate to home or the previously requested page
        const from = (location.state as any)?.from?.pathname || `/${RouteDefine.home}`;
        setTimeout(() => {
          navigate(from);
        }, timeout + 1000);
      } else {
        // Handle validation errors
        setNotification({
          open: true,
          message: response.data.message || 'Connection failed',
          severity: 'error'
        });
      }
    } catch (error: any) {
      console.error('Connection error:',error.errors.message);
      setNotification({
        open: true,
        message: error?.errors?.message || error.message || 'An error occurred while connecting',
        severity: 'error'
      });
    } finally {
      setIsConnecting(false);
    }
  };
  console.log(process.env.REACT_APP_API_ECONOMIC_REQUEST_ACCESS_URL);
  return (
    <Layout>
      <Container maxWidth="md">
        <LoadingOverlay open={loading || isConnecting} />
        <Box sx={{ py: 4, minHeight: 'calc(100vh - 200px)' }}>
          <PageTitle 
            align="center" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: 'primary.main',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
              mb: 4
            }}
          >
            Connection Setup
          </PageTitle>

          <Paper elevation={3} sx={{ p: 3, mt: 4 }}>

            <SectionTitle>e-conomic</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField 
                  fullWidth 
                  label="Token" 
                  value={economicToken} 
                  onChange={(e) => setEconomicToken(e.target.value)}
                  required
                  disabled={loading || isConnecting}
                  size="small"  
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => window.open(process.env.REACT_APP_API_ECONOMIC_REQUEST_ACCESS_URL, '_blank')}
                  disabled={loading || isConnecting}
                  sx={{ minWidth: 120, height: 40 }}
                >
                  Get Token
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={rememberConnection}
                    onChange={(e) => setRememberConnection(e.target.checked)}
                    disabled={loading || isConnecting}
                  />
                }
                label="Remember connection"
              />
              <Tooltip title="Connect to Dinero and e-conomic">
                <Button
                  ref={connectButtonRef}
                  variant="contained"
                  color="primary"
                  onClick={handleConnect}
                  disabled={loading || isConnecting}
                  sx={{ minWidth: 120 }}
                >
                  {isConnecting ? 'Connecting...' : 'Connect'}
                </Button>
              </Tooltip>
            </Box>
          </Paper>
        </Box>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default SetupConnectionPage; 